import {
  generateDisneyCluster,
  resetHotelAvailability,
  setHotelAvailability,
} from '@store/hotel-availability/hotelAvailability.actions';
import { AfterViewInit, Component, OnInit } from '@angular/core';
import {
  AnalyticsService,
  DetailService,
  DeviceService,
  InjectHeaderAndFooterWCService,
  RecommendedHotelsService,
  ReqService,
  SearchStateService,
} from '@core/services';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from '@am/alliance-service';
import { setHotelStaticInfo } from '@store/hotelStaticInfo.actions';
import { Ga4Service } from '@core/services/ga4.service';
import { setSearchValues } from '@store/app-context/app-context.actions';
import { cloneDeep } from 'lodash';
import { CustomLoaders, LoadingService } from '@core/services/loading/loading.service';
import { HotelDetailMobileComponent } from './components/hotel-detail-mobile/hotel-detail-mobile.component';
import { HotelDetailComponent } from './components/hotel-detail/hotel-detail.component';

@Component({
    selector: 'detail-container',
    templateUrl: './detail.component.html',
    styleUrls: ['./detail.component.scss'],
    standalone: true,
    imports: [
        HotelDetailComponent,
        HotelDetailMobileComponent,
    ],
})
export class DetailComponent implements OnInit, AfterViewInit {
  public locale: string;
  public isLanding: boolean;
  public hotelStaticInfo: any;
  public isMobile: boolean;
  public brand: string;
  public app: string;
  public channel: string;
  public country: string;
  public searchedEntity: any;
  public busy: boolean;
  public hotelAvailabilityInfo: any;
  public accommodationFee: any;
  public recommendedHotels: any;
  public error: any = null;

  constructor(
    private searchStateService: SearchStateService,
    private reqService: ReqService,
    private detailService: DetailService,
    private recommendedHotelsService: RecommendedHotelsService,
    private analyticsService: AnalyticsService,
    private route: ActivatedRoute,
    private router: Router,
    private deviceService: DeviceService,
    public injectHeaderAndFooterWCService: InjectHeaderAndFooterWCService,
    private store: Store<AppState>,
    private ga4Service: Ga4Service,
    private readonly loadingService: LoadingService,
  ) {
    this.initializeData();
  }

  ngOnInit() {
    window.scrollTo(0, 0);
    this.isMobile = this.deviceService.isMobile();
    this.fetchDetail();
  }

  ngAfterViewInit() {
    this.injectHeaderAndFooterWCService.showWC();
  }

  public resetState(currencyCode?: 'USD' | 'ARS') {
    this.loadingService.hideCustomLoader(CustomLoaders.HOTEL_AVAILABILITY);
    const params = {};
    if (currencyCode !== undefined) {
      this.searchStateService.setCurrencyCode(currencyCode);
    }

    // ToDO: Improve this
    this.searchStateService
      .getQueryString()
      .slice(1)
      .split('&')
      .map((item) => item.split('='))
      .forEach((item) => {
        params[item[0]] = item[1];
      });
    this.router
      .navigate(['.'], { relativeTo: this.route, queryParams: params })
      .then(() => this.searchStateService.setState());
    this.store.dispatch(resetHotelAvailability());
    this.fetchDetail();
  }

  private hasStaticInfo() {
    this.searchedEntity = {
      id: this.hotelStaticInfo.id,
      name: this.hotelStaticInfo.name,
      type: 'hotel',
    };

    this.analyticsService.trackPrDetail({
      cityId: this.hotelStaticInfo.city.id,
      hotelId: this.hotelStaticInfo.id,
      hotelName: this.hotelStaticInfo.name,
    });

    const origin = this.searchStateService.getParamsCurrentURL().origin;

    if (origin) {
      this.detailService.getEntity('CITY', origin).subscribe(
        (data) => {
          this.hotelStaticInfo.city = data;
          this.hotelStaticInfo.city.id = origin;
        },
        (error) => console.error(error),
      );
    }

    if (this.locale === 'pt-BR') {
      this.accommodationFee = this.hotelStaticInfo.description.hotel_accommodation_fee;
    }

    this.recommendedHotelsService
      .getRecommendedHotels(
        this.hotelStaticInfo.id,
        this.searchStateService.getQueryString() +
          (this.isLanding ? '&isLanding=true' : '&isLanding=false'),
      )
      .subscribe(
        (data) => (this.recommendedHotels = data),
        (error) => console.error(error),
      );
  }

  private fetchDetail() {
    this.searchStateService.setState();
    this.loadingService.showCustomLoader(CustomLoaders.HOTEL_AVAILABILITY);
    if (!this.isLanding) {
      this.busy = true;
      const chain = this.hotelStaticInfo.chain && this.hotelStaticInfo.chain.code;
      this.hasStaticInfo();
      this.store.dispatch(resetHotelAvailability());
      this.detailService.getHotelDetail(chain).subscribe(
        (response) => {
          this.busy = false;
          this.hotelAvailabilityInfo = response;
          this.analyticsService.newTrackPrDetail({
            ...this.hotelAvailabilityInfo,
            ...this.hotelStaticInfo,
          });
          this.loadingService.hideCustomLoader(CustomLoaders.HOTEL_AVAILABILITY);
          this.store.dispatch(
            setHotelAvailability({
              hotelAvailability: cloneDeep(this.hotelAvailabilityInfo),
            }),
          );
          this.store.dispatch(generateDisneyCluster());
          this.ga4TrackingEvent();

          if (this.error !== null) {
            this.error = null;
          }
        },
        (error) => {
          this.busy = false;
          this.error = error;
          this.analyticsService.newTrackPrDetail({ ...this.hotelStaticInfo });
          this.loadingService.hideCustomLoader(CustomLoaders.HOTEL_AVAILABILITY);
        },
      );
    } else {
      this.busy = false;
      this.hotelAvailabilityInfo = false;
      this.loadingService.hideCustomLoader(CustomLoaders.HOTEL_AVAILABILITY);
    }
  }

  private initializeData() {
    this.locale = this.reqService.getLocale();
    this.isLanding = this.reqService.isLanding;
    this.brand = this.reqService.getBrand();
    this.channel = this.reqService.getChannel();
    this.country = this.reqService.getCountry();
    this.hotelStaticInfo = this.reqService.getHotelStaticInfo();

    this.store.dispatch(setHotelStaticInfo({ hotelStaticInfo: this.hotelStaticInfo }));
    this.store.dispatch(
      setSearchValues({ params: this.searchStateService.getLocationSearch() }),
    );
  }

  private ga4TrackingEvent() {
    const {
      recommended,
      best_disney_room_cluster,
      best_room_cluster,
      country,
      city,
      amenities,
    } = this.hotelAvailabilityInfo;
    const rate =
      best_room_cluster !== undefined
        ? best_room_cluster.rates[0]
        : best_disney_room_cluster
          ? best_disney_room_cluster.disney_meal_plans[0].rates[0]
          : null;
    const isPriorityProvider = !!best_room_cluster?.prioritized_by_rate_filter;
    this.ga4Service.view_item(
      recommended,
      country,
      city,
      rate,
      amenities,
      this.hotelAvailabilityInfo,
      isPriorityProvider,
    );
  }
}
